/**
 * MyVisitsMap — platform React component for the caregiver "My Visits" map view.
 *
 * Renders a Google Map with:
 *   - Sequenced visit markers (ordered by scheduled time)
 *   - Current-user-location marker (via Geolocation API)
 *   - Driving-route overlay between all waypoints
 *   - Tap-to-open info-card overlay for each visit
 *
 * Consumed as a React island inside the template engine tree.
 * Register once at runtime setup via:
 *   tplEngine.registerComponent("myVisitsMap", MyVisitsMap)
 *
 * Then reference in the tree:
 *   "my-visits-page[my-visits-container[myVisitsMap]]"
 *
 * Props:
 *   @prop {Array}    visits    - Raw visit objects from the API.
 *   @prop {Function} onMetrics - Callback({ distance: km, duration: min }) when route loads.
 *   @prop {Object}   tplEngine - Template engine instance (used by the inner InfoCard renderer).
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react"
import { GoogleMap, DirectionsRenderer, useJsApiLoader, OverlayView } from "@react-google-maps/api"
import { getCurrentLocation } from "../../utils/gps"
import { showError, showInfo } from "../../utils/snackbar"

// Stable reference — must be outside component to prevent useJsApiLoader from reloading

// ── Private helpers ───────────────────────────────────────────────────────────

/**
 * Maps a visit status key to a human-readable label.
 *
 * @param {string} status - Raw status string from the API.
 * @returns {string} Display label.
 */
const getStatusLabel = (status) => {
    const map = {
        scheduled: "Scheduled",
        in_progress: "In Progress",
        completed: "Completed",
        missed: "Missed",
        cancelled: "Cancelled",
    }
    return map[status?.toLowerCase()] || status
}

/**
 * Returns the CSS class string for the status badge on the info popup.
 *
 * @param {string} status - Raw status string from the API.
 * @returns {string} CSS class string.
 */
const getBadgeClass = (status) => {
    const s = (status || "").toLowerCase()
    if (s === "completed") return "e-caregiver-detail-route-card-badge completed"
    if (s === "missed") return "e-caregiver-detail-route-card-badge missed"
    if (s === "cancelled") return "e-caregiver-detail-route-card-badge cancelled"
    return "e-caregiver-detail-route-card-badge scheduled"
}

/**
 * Shapes a visit object into the data contract expected by the my-visits-popup template.
 *
 * @param {Object} visit - Sequenced visit object (includes sequence, careTime, etc.).
 * @returns {Object} Template data for my-visits-popup.
 */
const mapVisitToInfoCardData = (visit) => {
    const careTimeLabel =
        visit.careTime >= 60
            ? `${Math.floor(visit.careTime / 60)}h ${visit.careTime % 60}m care time`
            : `${visit.careTime || 0} min care time`

    const badgeHtml = visit.status
        ? `<span class="${getBadgeClass(visit.status)}">${voiceiiTags(getStatusLabel(visit.status))}</span>`
        : ""

    const careTimeRow =
        visit.careTime > 0
            ? `<section class="e-caregiver-detail-route-card-row"><span>⏱</span><span>${careTimeLabel}</span></section>`
            : ""

    const scheduledTime = new Date(visit.date_scheduled).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
    })

    return {
        sequence: visit.sequence,
        name: visit.name || "-",
        badgeHtml,
        scheduledTime,
        careTimeRow,
        address: visit.address || "-",
    }
}

// ── MyVisitsMap ───────────────────────────────────────────────────────────────

const MyVisitsMap = ({ visits = [], onMetrics, tplEngine }) => {
    const LIBRARIES = ["drawing", "places"]
    const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }
    const voiceiiTags = tplEngine?.translate || ((text) => text)

    const { isLoaded } = useJsApiLoader({
        id: "drawing-manager-example",
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries: LIBRARIES,
    })

    const [userLocation, setUserLocation] = useState(null)
    const [directions, setDirections] = useState(null)
    const [selectedMarker, setSelectedMarker] = useState(null)
    const mapRef = useRef(null)

    /**
     * Fetches the user's current GPS coordinates and optionally recenters the map.
     *
     * @param {boolean} shouldPanToMap - Whether to pan/zoom the map on success.
     * @returns {Promise<void>}
     */
    const fetchUserLocation = useCallback(
        async (shouldPanToMap = false) => {
            try {
                const coords = await getCurrentLocation()
                const location = { lat: coords.latitude, lng: coords.longitude }
                setUserLocation(location)

                if (shouldPanToMap && mapRef.current) {
                    mapRef.current.panTo(location)
                    mapRef.current.setZoom(12)
                }
            } catch (error) {
                showError(voiceiiTags(error?.message || "Unable to get current location."))
            }
        },
        [voiceiiTags],
    )

    // ── SmartInfoCard — local component, closes over tplEngine ────────────

    /**
     * Renders the visit info popup overlay using the my-visits-popup template.
     * Automatically adjusts position if the card would overflow the map boundary.
     *
     * @param {Object}   visit   - Visit data (sequenced).
     * @param {Function} onClose - Called when the user dismisses the card.
     */
    const SmartInfoCard = ({ visit, onClose }) => {
        const cardRef = useRef(null)

        useEffect(() => {
            if (!cardRef.current) return
            const el = cardRef.current
            const mapContainer = el.closest(".e-caregiver-detail-route-card-map")
            if (!mapContainer) return
            const mapRect = mapContainer.getBoundingClientRect()
            const cardRect = el.getBoundingClientRect()
            if (cardRect.top < mapRect.top + 8) {
                el.style.marginTop = `${cardRect.height + 60}px`
            }
        }, [visit])

        useEffect(() => {
            if (!cardRef.current) return

            tplEngine
                .render("my-visits-popup", mapVisitToInfoCardData(visit))
                .then((html) => {
                    if (cardRef.current) cardRef.current.innerHTML = html
                })
                .catch((err) => console.error("InfoCard render failed:", err))

            const handler = (e) => {
                const target = e.target.closest("[data-action]")
                if (!target) return
                if (target.getAttribute("data-action") === "close-info-card") onClose()
            }
            cardRef.current.addEventListener("click", handler)
            return () => cardRef.current?.removeEventListener("click", handler)
        }, [visit, onClose])

        return <section ref={cardRef} />
    }

    // ── Geolocation ───────────────────────────────────────────────────────

    useEffect(() => {
        fetchUserLocation(false)
    }, [fetchUserLocation])

    useEffect(() => {
        if (!isLoaded || !userLocation || !mapRef.current) return

        mapRef.current.panTo(userLocation)
        mapRef.current.setZoom(12)
    }, [isLoaded, userLocation])

    // ── Visit sequencing + GPS filtering ─────────────────────────────────

    /**
     * Extracts a {lat, lng} GPS object from a visit, returning null if absent.
     *
     * @param {Object} visit - Raw visit object.
     * @returns {{lat: number, lng: number}|null}
     */
    const getPatientGPS = (visit) => {
        const lat = visit?.location?.lat
        const lng = visit?.location?.lng
        if (!lat || !lng) return null
        return { lat: Number(lat), lng: Number(lng) }
    }

    const sequencedVisits = useMemo(() => {
        return [...visits]
            .sort((a, b) => new Date(a.date_scheduled) - new Date(b.date_scheduled))
            .map((v, i) => ({ ...v, sequence: i + 1, gps: getPatientGPS(v) }))
    }, [visits])

    const gpsVisits = useMemo(() => sequencedVisits.filter((v) => v.gps), [sequencedVisits])

    // ── Map load callback — fits bounds to all markers ────────────────────

    /**
     * Called by GoogleMap once the map instance is ready.
     * Fits the viewport to include all visit markers and the user location.
     *
     * @param {google.maps.Map} map - The Google Map instance.
     */
    const onMapLoad = useCallback(
        (map) => {
            mapRef.current = map
            if (userLocation) {
                map.setCenter(userLocation)
                map.setZoom(12)
            } else if (gpsVisits.length > 1) {
                const bounds = new window.google.maps.LatLngBounds()
                gpsVisits.forEach((v) => bounds.extend(v.gps))
                map.fitBounds(bounds)
            } else if (gpsVisits.length === 1) {
                map.setCenter(gpsVisits[0]?.gps)
                map.setZoom(12)
            } else {
                map.setCenter(DEFAULT_CENTER)
            }

            // ── Current location custom control ───────────────────────────
            const controlDiv = document.createElement("div")
            controlDiv.style.marginRight = "10px"
            controlDiv.style.marginBottom = "-70px"
            controlDiv.style.cursor = "pointer"

            const controlBtn = document.createElement("button")
            controlBtn.type = "button"
            controlBtn.title = "Go to current location"
            controlBtn.style.backgroundColor = "#fff"
            controlBtn.style.border = "none"
            controlBtn.style.borderRadius = "2px"
            controlBtn.style.boxShadow = "0 1px 4px rgba(0,0,0,0.3)"
            controlBtn.style.width = "40px"
            controlBtn.style.height = "40px"
            controlBtn.style.display = "flex"
            controlBtn.style.alignItems = "center"
            controlBtn.style.justifyContent = "center"
            controlBtn.style.cursor = "pointer"
            controlBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/><line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/></svg>`

            controlBtn.addEventListener("click", () => {
                fetchUserLocation(true)
            })

            controlDiv.appendChild(controlBtn)
            map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(controlDiv)
        },
        [fetchUserLocation, gpsVisits, userLocation],
    )

    // ── Directions + route metrics ────────────────────────────────────────

    useEffect(() => {
        if (!isLoaded || !userLocation || gpsVisits.length === 0) return

        const directionsService = new window.google.maps.DirectionsService()
        const waypoints = gpsVisits.slice(0, -1).map((v) => ({ location: v.gps, stopover: true }))

        directionsService.route(
            {
                origin: userLocation,
                destination: gpsVisits[gpsVisits.length - 1].gps,
                waypoints,
                travelMode: window.google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                if (status === "OK") {
                    setDirections(result)
                    const metrics = result.routes[0].legs.reduce(
                        (acc, leg) => {
                            acc.distance += leg.distance.value
                            acc.duration += leg.duration.value
                            return acc
                        },
                        { distance: 0, duration: 0 },
                    )
                    onMetrics?.({
                        distance: metrics.distance / 1000,
                        duration: metrics.duration / 60,
                    })
                } else {
                    console.error("Route failed:", status)
                }
            },
        )
    }, [isLoaded, userLocation, gpsVisits])

    // ── Render ────────────────────────────────────────────────────────────

    if (!isLoaded) return <p>{voiceiiTags("Loading map...")}</p>

    return (
        <GoogleMap
            mapContainerStyle={{ width: "100%", height: "78vh" }}
            onLoad={onMapLoad}
            zoom={6}
            onClick={() => setSelectedMarker(null)}
        >
            {directions && (
                <DirectionsRenderer directions={directions} options={{ suppressMarkers: true }} />
            )}

            {userLocation && (
                <OverlayView position={userLocation} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <span className="e-caregiver-detail-route-card-marker">
                        {voiceiiTags("You")}
                    </span>
                </OverlayView>
            )}

            {gpsVisits.map((visit) => (
                <OverlayView
                    key={visit.entity_id}
                    position={visit.gps}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                    <span
                        className={`e-caregiver-detail-route-card-marker ${
                            selectedMarker?.entity_id === visit.entity_id ? "active" : ""
                        }`}
                        onClick={(e) => {
                            e.stopPropagation()
                            setSelectedMarker(visit)
                        }}
                    >
                        {visit.sequence}
                    </span>
                </OverlayView>
            ))}

            {selectedMarker && (
                <OverlayView
                    position={selectedMarker.gps}
                    mapPaneName={OverlayView.FLOAT_PANE}
                    getPixelPositionOffset={(width, height) => ({
                        x: -width / 2,
                        y: -(height + 30),
                    })}
                >
                    <SmartInfoCard visit={selectedMarker} onClose={() => setSelectedMarker(null)} />
                </OverlayView>
            )}
        </GoogleMap>
    )
}

export default MyVisitsMap
